import environment from 'customer-flows/config/environment'
import { tracked } from '@glimmer/tracking'

const LogLevel: { [key: number]: string } = {
  0: 'none',
  1: 'error',
  2: 'warn',
  3: 'info',
  4: 'debug'
}

type LogData = Record<string, unknown>

export type LogEntry = {
  level: string
  message: string
  timestamp: string
  callerTrace: string
  data: LogData
}

export type LogTransport = {
  debug?: (_entry: LogEntry) => void
  info?: (_entry: LogEntry) => void
  warn?: (_entry: LogEntry) => void
  error?: (_entry: LogEntry) => void
}

class LoggerClass {
  @tracked logLevel = environment.logLevel ?? 0
  @tracked logs: LogEntry[] = []

  consoleEnabled = !environment.isTesting // set by test env to test this class

  // subsribed event handlers (ie. sentry)
  transports: Set<LogTransport> = new Set()

  setLogLevel(level: number): void {
    if (level < 0 || level > 4) {
      this.error(`Invalid log level: ${level}`)
      return
    }

    this.logLevel = level
  }

  /**
   * Subscribes a transport to the logger. The transport will be called for every log entry.
   */
  addTransport(transport: LogTransport): void {
    this.transports.add(transport)
  }

  /**
   * Unsubscribes a transport from the logger.
   */
  removeTransport(transport: LogTransport): void {
    this.transports.delete(transport)
  }

  debug(message: string, data: LogData = {}): void {
    this._log('debug', message, data)
  }

  info(message: string, data: LogData = {}): void {
    this._log('info', message, data)
  }

  warn(message: string, data: LogData = {}): void {
    this._log('warn', message, data)
  }

  error(message: string, data: LogData = {}): void {
    this._log('error', message, data)
  }

  private _log(level: string, message: string, data: LogData = {}) {
    if (!('logger' in window)) {
      ; (window as any).logger = this
    }

    const callerTrace = this._getCallerTrace()
    const formattedMessage = `${callerTrace} - ${message}`
    const timestamp = new Date().toISOString()
    const entry: LogEntry = { level, message, timestamp, callerTrace, data }

    this.logs = [...this.logs, entry]

    // find the index of the level
    const index = Object.values(LogLevel).findIndex((l) => l === level)
    const enabled = index <= this.logLevel && this.consoleEnabled

    if (enabled) {
      ; (console as any)[level](`[${level.toUpperCase()}] ${formattedMessage}`, data)
    }

    // call all transports
    for (const handler of this.transports) {
      try {
        handler[level as keyof LogTransport]?.(entry)
      } catch (e) {
        if (enabled) console.warn('[Logger] handler threw:', e)
      }
    }
  }

  private _getCallerTrace(skipFile = 'logger.ts'): string {
    const stack = new Error().stack
    if (!stack) return 'unknown'

    const lines = stack.split('\n')

    for (const line of lines) {
      if (!line.includes(skipFile)) {
        const match = line.match(/\(?([^\s)]+:\d+:\d+)\)?$/)
        if (match?.[1]) {
          let url = match[1]

          // Remove protocol + host
          url = url.replace(/^https?:\/\/[^/]+\/?/, '')

          // Remove query params
          url = url.replace(/\?.*?(?=:)/, '')

          // Remove webpack://customer-flows/.
          url = url.replace(/^webpack:\/\/customer-flows\/\./, '')

          return url
        }
      }
    }

    return 'unknown'
  }
}

const Logger = new LoggerClass()
export default Logger
